import {DeleteOutlined, PlusOutlined} from '@ant-design/icons'
import {Button, Col, Form, Input, Row, Select} from 'antd'
import {ReportFilter} from 'common/api/api.ts'
import {BrowserSelector} from 'common/components/dictionary/BrowserSelector.tsx'
import {ConnectionTypeSelector} from 'common/components/dictionary/ConnectionTypeSelector.tsx'
import {CountryByIsoSelector} from 'common/components/dictionary/CountryByIsoSelector.tsx'
import {DeviceTypeSelector} from 'common/components/dictionary/DeviceTypeSelector.tsx'
import {FormatTypeSelector} from 'common/components/dictionary/FormatTypeSelector.tsx'
import {OsSelector} from 'common/components/dictionary/OsSelector.tsx'
import {SiteTypeSelector} from 'common/components/dictionary/SiteTypeSelector.tsx'
import {AdGroupSelector} from 'common/components/reloadable/AdGroupSelector.tsx'
import {AdUnitSelector} from 'common/components/reloadable/AdUnitSelector.tsx'
import {AdvertiserSelector} from 'common/components/reloadable/AdvertiserSelector.tsx'
import {AgencySelector} from 'common/components/reloadable/AgencySelector.tsx'
import {CampaignSelector} from 'common/components/reloadable/CampaignSelector.tsx'
import {IspOrganizationSelector} from 'common/components/reloadable/IspOrganizationSelector.tsx'
import {CreativeSelector} from 'common/components/reloadable/CreativeSelector.tsx'
import {DealSelector} from 'common/components/reloadable/DealSelector.tsx'
import {DspSelector} from 'common/components/reloadable/DspSelector.tsx'
import {PublisherSelector} from 'common/components/reloadable/PublisherSelector.tsx'
import {SiteSelector} from 'common/components/reloadable/SiteSelector.tsx'
import {SspSelector} from 'common/components/reloadable/SspSelector.tsx'
import {TrackerSelector} from 'common/components/reloadable/TrackerSelector.tsx'
import React from 'react'

export interface ReportFilterLine {
  dimension?: string
  value?: number | string | Array<number | string>
}

interface ReportFiltersComponentProps {
  allFilters?: ReportFilter[]
  selectedFilters: ReportFilterLine[]
  setSelectedFilters: (filters: ReportFilterLine[]) => void
  form: any
  tooltip?: string
}

const SINGLE_VALUE_DIMENSIONS = new Set<string>(['SiteType'])
const SINGLE_VALUE_NAMES = new Set<string>(['siteType'])

const filterTypeIsContains = (type?: ReportFilter.TypeEnum | string) => type === ReportFilter.TypeEnum.CONTAINS || type === 'CONTAINS'

const dimensionToSelector: Record<string, React.ComponentType<any>> = {
  AgencyId: AgencySelector,
  SspId: SspSelector,
  DspId: DspSelector,
  AdUnitId: AdUnitSelector,
  AdvertiserId: AdvertiserSelector,
  CampaignId: CampaignSelector,
  AdGroupId: AdGroupSelector,
  CreativeId: CreativeSelector,
  CountryIso3: CountryByIsoSelector,
  DeviceType: DeviceTypeSelector,
  Os: OsSelector,
  ConnectionType: ConnectionTypeSelector,
  Browser: BrowserSelector,
  FormatType: FormatTypeSelector,
  SiteType: SiteTypeSelector,
  TrackerId: TrackerSelector,
  SiteId: SiteSelector,
  DealId: DealSelector,
  PublisherId: PublisherSelector,
  Company: IspOrganizationSelector,
}

const nameToSelector: Record<string, React.ComponentType<any>> = {
  agencyIds: AgencySelector,
  sspIds: SspSelector,
  dspIds: DspSelector,
  adUnitIds: AdUnitSelector,
  advertiserIds: AdvertiserSelector,
  campaignIds: CampaignSelector,
  adGroupIds: AdGroupSelector,
  creativeIds: CreativeSelector,
  countryIso3: CountryByIsoSelector,
  deviceType: DeviceTypeSelector,
  os: OsSelector,
  connectionType: ConnectionTypeSelector,
  browser: BrowserSelector,
  formatType: FormatTypeSelector,
  siteType: SiteTypeSelector,
  trackerIds: TrackerSelector,
  siteIds: SiteSelector,
  dealIds: DealSelector,
  publisherIds: PublisherSelector,
  companyNames: IspOrganizationSelector,
}

export const ReportFiltersComponent: React.FC<ReportFiltersComponentProps> = ({
                                                                                allFilters,
                                                                                selectedFilters,
                                                                                setSelectedFilters,
                                                                                form,
                                                                                tooltip,
                                                                              }) => {
  const getFilterConfig = (dimension?: string) => allFilters?.find((filter) => filter.key === dimension)

  const addFilter = () => {
    setSelectedFilters([...selectedFilters, { dimension: undefined, value: undefined }])
  }

  const removeFilter = (index: number) => {
    const list = [...selectedFilters]
    list.splice(index, 1)
    setSelectedFilters(list.length ? list : [{ dimension: undefined, value: undefined }])
  }

  const handleDimensionChange = (dimension: string | undefined, index: number) => {
    const list = [...selectedFilters]
    list[index] = { dimension, value: undefined }
    form.setFieldsValue({
      filters: {
        [index]: { value: undefined },
      },
    })
    setSelectedFilters(list)
  }

  const handleValueChange = (val: any, index: number) => {
    const list = [...selectedFilters]
    list[index].value = val
    setSelectedFilters(list)
  }

  const renderValueSelector = (filter: ReportFilterLine, index: number) => {
    const { dimension, value } = filter
    if (!dimension) {
      return <Select disabled mode="multiple" placeholder="-select-" style={{ width: '100%' }} value={undefined} />
    }

    const filterConfig = getFilterConfig(dimension)
    if (filterTypeIsContains(filterConfig?.type)) {
      return (
        <Input
          allowClear
          placeholder="Contains substring"
          style={{ width: '100%' }}
          value={typeof value === 'string' ? value : undefined}
          onChange={(event) => handleValueChange(event.target.value, index)}
        />
      )
    }

    const SelectorComp = dimensionToSelector[dimension] || nameToSelector[dimension]
    if (!SelectorComp) {
      return <Select placeholder="Selector is not defined" options={[{ label: 'Selector is not defined' }]} />
    }

    const isSingle = SINGLE_VALUE_DIMENSIONS.has(dimension) || SINGLE_VALUE_NAMES.has(dimension)

    return (
      <SelectorComp
        multiple={!isSingle}
        placeholder="-select-"
        style={{ width: '100%' }}
        value={value}
        onChange={(v: any) => handleValueChange(v, index)}
      />
    )
  }

  return (
    <Form.Item label="Filters" tooltip={tooltip}>
      {selectedFilters.map((filter, index) => {
        const showRemove = selectedFilters.length > 1

        return (
          <div key={index} style={{ position: 'relative', marginBottom: 8 }}>
            <Row gutter={8} align="top">
              <Col flex="1">
                <Form.Item noStyle>
                  <Select
                    placeholder="Select Dimension to Filter"
                    options={allFilters?.map((f) => ({ label: f.title, value: f.key }))}
                    value={filter.dimension}
                    onChange={(value) => handleDimensionChange(value, index)}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col flex="1">
                <Form.Item noStyle>{renderValueSelector(filter, index)}</Form.Item>
              </Col>
            </Row>

            {showRemove && (
              <Button
                icon={<DeleteOutlined />}
                type="text"
                onClick={() => removeFilter(index)}
                style={{
                  position: 'absolute',
                  right: -40,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: 0,
                }}
                aria-label="Remove filter"
              />
            )}
          </div>
        )
      })}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="link"
          onClick={addFilter}
          style={{ paddingRight: 0, height: 'fit-content', color: '#334D97' }}
          aria-label="Add filter"
        >
          <PlusOutlined style={{ marginRight: '-12px' }} /> <span style={{ fontSize: '10px' }}>Add Filter</span>
        </Button>
      </div>
    </Form.Item>
  )
}
